<template>
  <div
    v-show="ready"
    ref="tree__item"
    class="timeline-item"
    :class="{
      ...itemClass,
      'timeline-item-icon': icon !== undefined,
      'timeline-item--observe-offset': isObservedOffset
    }"
  >
    <div
      class="timeline-item--content"
      :class="{
        'timeline-item--content-has-icon-branch': icon && icon.branch && (icon.text || icon.faIcon)
      }"
    >
      <div
        v-if="icon && icon.branch"
        ref="tree__item-icon"
        class="timeline-item--icon"
        :class="itemIconClass"
      ></div>

      <div
        v-if="icon && !icon.branch"
        ref="tree__item--icon-wrapper"
        class="timeline-item--icon-wrapper"
        :class="itemIconWrapperClass"
      >
        <span v-if="icon.text" ref="tree__item--icon-text" class="tree__item--icon-text">{{
          icon.text
        }}</span>
        <span v-else-if="icon.faIcon" ref="tree__item--icon-fa" class="tree__item--icon-fa">
          <font-awesome-icon :icon="icon.faIcon" />
        </span>
      </div>

      <v-component
        :is="component"
        v-if="component"
        ref="tree__item--component"
        v-bind="{ ...componentProps }"
      >
      </v-component>

      <div v-else>
        <div
          v-if="icon && icon.branch && (icon.text || icon.faIcon)"
          ref="tree__item--icon-wrapper-branch"
          class="timeline-item--icon-wrapper-branch"
          :class="itemIconWrapperClass"
        >
          <span v-if="icon.text" ref="tree__item--icon-text" class="tree__item--icon-text">{{
            icon.text
          }}</span>
          <span v-else-if="icon.faIcon" ref="tree__item--icon-fa" class="tree__item--icon-fa">
            <font-awesome-icon :icon="icon.faIcon" />
          </span>
        </div>

        <div class="container__tree-title d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-end">
            <div v-if="!hideShift" class="d-flex flex-column mr-2 px-1">
              <b-button
                variant="link"
                class="p-0 button__shift-up"
                :disabled="disableShiftUp"
                @click="onShiftUpClicked"
              >
                <font-awesome-icon icon="angle-up" class="mb-1" />
              </b-button>

              <b-button
                variant="link"
                class="p-0 button__shift-down"
                :disabled="disableShiftDown"
                @click="onShiftDownClicked"
              >
                <font-awesome-icon icon="angle-down" />
              </b-button>
            </div>

            <h4
              ref="text__tree-title"
              class="timeline-item--label text__tree-title mb-2 font-weight-bold"
              :class="titleClass"
              v-text="title"
            ></h4>
          </div>

          <base-ellipsis
            v-if="!hideSetting"
            ref="dropdown__tree"
            class="button__dropdown-toggle"
            :disabled="disabled"
          >
            <b-dropdown-item v-if="edittable" @click="$emit('edit')"> Edit </b-dropdown-item>
            <b-dropdown-item v-if="deleteable" @click="$emit('delete')"> Delete </b-dropdown-item>
          </base-ellipsis>
        </div>

        <div class="container__tree-content">
          <p
            ref="text__tree-sub-title"
            class="text-black text__tree--sub-title"
            :class="subTitleClass"
            v-text="subTitle"
          ></p>

          <div v-if="!hideDescription">
            <div
              v-show="active"
              ref="text__tree-description"
              v-safe-html="description"
              class="text-dark text__tree-description"
              :class="descriptionClass"
            ></div>

            <b-button
              v-if="description"
              ref="btn__tree-label-toggler"
              variant="link"
              size="sm"
              class="px-0 border-0"
              :class="classButtonToggler"
              @click="toggleActive"
            >
              <span ref="text__tree-label-toggler" class="text__tree-label-toggler">{{
                labelToggler
              }}</span>
            </b-button>
          </div>
        </div>
      </div>

      <slot></slot>
    </div>
  </div>
</template>

<script>
import BaseEllipsis from '@/components/base-components/BaseEllipsis';

export default {
  components: {
    BaseEllipsis
  },
  props: {
    icon: {
      type: Object,
      default: () => ({})
    },

    component: {
      type: Object,
      default: null
    },

    componentProps: {
      type: Object,
      default: () => ({})
    },

    title: {
      type: String,
      default: ''
    },

    subTitle: {
      type: String,
      default: ''
    },

    description: {
      type: String,
      default: ''
    },

    itemClass: {
      type: [String, Array, Object],
      default: () => ({})
    },

    titleClass: {
      type: [String, Array, Object],
      default: () => ({})
    },

    subTitleClass: {
      type: [String, Array, Object],
      default: () => ({})
    },

    descriptionClass: {
      type: [String, Array, Object],
      default: () => ({})
    },

    togglerActiveClass: {
      type: String,
      default: 'button__show'
    },

    togglerInActiveClass: {
      type: String,
      default: 'button__hide'
    },

    togglerLabel: {
      type: String,
      default: 'description'
    },

    disabled: {
      type: [Number, Boolean],
      default: false
    },

    edittable: {
      type: Boolean,
      default: true
    },

    deleteable: {
      type: Boolean,
      default: true
    },

    disableShiftUp: {
      type: Boolean,
      default: false
    },

    disableShiftDown: {
      type: Boolean,
      default: false
    },

    hideShift: {
      type: Boolean,
      default: false
    },

    hideSetting: {
      type: Boolean,
      default: false
    },

    hideDescription: {
      type: Boolean,
      default: false
    },

    isObservedOffset: {
      type: Boolean,
      default: false
    },

    index: {
      type: [String, Number],
      default: null
    },

    parentIndex: {
      type: [String, Number],
      default: null
    },

    activeState: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      ready: false,
      active: this.activeState[`${this.parentIndex}_${this.index}`] || false,
      timeLineItemStyles: {}
    };
  },

  computed: {
    classButtonToggler() {
      return {
        [this.active ? this.togglerActiveClass : this.togglerInActiveClass]: true
      };
    },

    labelToggler() {
      let actionLabel = this.active ? 'Hide' : 'Show';

      return `${actionLabel} ${this.togglerLabel}`;
    },

    itemIconWrapperClass() {
      const { variant = 'primary', color = 'white', className = {} } = this.icon;

      return {
        [`bg-${variant}`]: true,
        [`text-${color}`]: true,
        ...className
      };
    },

    itemIconClass() {
      const { branch = false } = this.icon;

      return {
        'timeline-item--branch': branch
      };
    }
  },

  watch: {
    active: 'updateState'
  },

  mounted() {
    this.updateState();

    this.ready = true;
  },

  methods: {
    toggleActive() {
      this.active = !this.active;
    },

    onShiftUpClicked() {
      this.$emit('shift:up');
    },

    onShiftDownClicked() {
      this.$emit('shift:down');
    },

    updateState(active) {
      this.$nextTick(() => {
        if (this.isObservedOffset && this.$refs.tree__item) {
          const { offsetHeight } = this.$refs.tree__item;

          this.$set(this.timeLineItemStyles, 'height', `${offsetHeight}px`);

          this.$emit('update:state', {
            offset: offsetHeight,
            isActive: active
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.timeline-item {
  &:last-child {
    position: absolute;
    padding-top: 0;
    // padding-bottom: 0 !important;
    width: 100%;
  }

  &--branch {
    background: transparent;
    border-bottom: 4px solid #dfe0ea;
    border-left: 4px solid #dfe0ea;
    width: 25px;
    border-radius: 10px;
    left: -32px;
    top: -1px;
    border-top-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--icon-wrapper {
    top: -10px;
  }

  .container__tree-title {
    position: relative;
    top: -10px;
  }

  .button__shift-up,
  .button__shift-down {
    border: none;
    font-size: 0.83125rem;
    line-height: 1.15;
  }
}

.timeline-item-icon {
  padding: 10px 0 15px 46px !important;
  margin-bottom: 0 !important;
}

// .timeline-item--content {
//   padding-top: 0.5rem;
//   padding-bottom: 1rem;
// }

.timeline-item--icon-wrapper-branch {
  border-radius: 0.75rem;
  width: 34px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  display: block;
  position: absolute;
  left: -5px;
  top: -5px;
}

.timeline-item--content-has-icon-branch {
  padding-left: 40px;
}
</style>

<style lang="scss">
.timeline-item {
  p {
    margin-bottom: 1rem;
  }
}
</style>
